import { faGripVertical, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";

import { BackLink } from "../components/back-link";
import { Button } from "../components/button";
import { FileUpload } from "../components/file-upload";
import { Form } from "../components/form";
import { Input } from "../components/input";
import { Layout } from "../components/layout";
import { useView } from "../queries/view";
import {
  addImage,
  deleteImage,
  updateImage,
  updateView,
} from "../services/view";
import { useConnection } from "../stores/connection";
import { Image as ImageType } from "../types/image";

const MAX_IMAGE_PIXEL_SIZE = 200;
const REORDER_TRANSITION = "transform 200ms cubic-bezier(0.2, 0, 0, 1)";

type ItemLayout = {
  top: number;
  height: number;
};

function getPreviewOrder(length: number, fromIndex: number, toIndex: number) {
  const order = Array.from({ length }, (_, index) => index);
  const [removed] = order.splice(fromIndex, 1);
  order.splice(toIndex, 0, removed);
  return order;
}

function getItemShifts({
  layouts,
  draggedIndex,
  dropIndex,
}: {
  layouts: ItemLayout[];
  draggedIndex: number | null;
  dropIndex: number | null;
}) {
  if (
    draggedIndex === null ||
    dropIndex === null ||
    draggedIndex === dropIndex ||
    layouts.length === 0
  ) {
    return layouts.map(() => 0);
  }

  const previewOrder = getPreviewOrder(layouts.length, draggedIndex, dropIndex);

  return layouts.map((layout, index) => {
    const previewPosition = previewOrder.indexOf(index);
    const targetTop = layouts[previewPosition]?.top ?? layout.top;
    return targetTop - layout.top;
  });
}

function getDropIndex({
  layouts,
  pointerY,
}: {
  layouts: ItemLayout[];
  pointerY: number;
}) {
  if (layouts.length === 0) {
    return 0;
  }

  for (let index = 0; index < layouts.length; index += 1) {
    const layout = layouts[index];
    if (!layout) {
      continue;
    }
    const midpoint = layout.top + layout.height / 2;
    if (pointerY < midpoint) {
      return index;
    }
  }

  return layouts.length - 1;
}

export function Edit() {
  const { code } = useParams();
  const { connection } = useConnection();

  if (!code) {
    throw new Error("Code is required");
  }

  const { data, isLoading: isViewLoading } = useView(code, "admin");
  const view = data?.view;

  const [name, setName] = useState(view?.name ?? "");
  const [images, setImages] = useState(view?.images ?? []);
  const [loading, setLoading] = useState<boolean>(false);

  const isDisabled = isViewLoading || loading;

  useEffect(() => {
    setName(view?.name ?? "");
    setImages(view?.images ?? []);
  }, [view?.name, view?.images]);

  const onReorder = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) {
      return;
    }

    const previousImages = images;
    const reordered = [...images];
    const [movedImage] = reordered.splice(fromIndex, 1);
    if (!movedImage) {
      return;
    }
    reordered.splice(toIndex, 0, movedImage);

    const nextImages = reordered.map((image, index) => ({
      ...image,
      order: index + 1,
    }));

    setImages(nextImages);
    setLoading(true);
    try {
      const updates = nextImages.filter((image) => {
        const previousImage = previousImages.find(
          (currentImage) => currentImage.id === image.id,
        );
        return previousImage && previousImage.order !== image.order;
      });

      await Promise.all(
        updates.map((image) =>
          updateImage({
            connection,
            imageId: image.id.toString(),
            image: { order: image.order },
          }),
        ),
      );
    } catch (error: unknown) {
      setImages(previousImages);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Layout>
      <BackLink />
      <div className="mx-auto mt-10 flex w-full max-w-[500px] flex-grow flex-col items-center gap-5">
        <Form
          className="flex gap-2 items-end"
          onsubmit={() => {
            (async () => {
              setLoading(true);
              try {
                await updateView({ connection, code, view: { name } });
              } catch (error: unknown) {
                console.error(error);
              } finally {
                setLoading(false);
              }
              setLoading(false);
            })();
          }}
        >
          <Input
            disabled={isDisabled}
            label="Name"
            name="name"
            placeholder="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button disabled={isDisabled} loading={loading} type="submit">
            Save
          </Button>
        </Form>
        {images.length > 0 && (
          <SortableImageList
            images={images}
            isDisabled={isDisabled}
            onDelete={(deletedImage) => {
              setImages(
                images.filter((currentImage) => {
                  return currentImage.id !== deletedImage.id;
                }),
              );
            }}
            onReorder={(fromIndex, toIndex) => {
              void onReorder(fromIndex, toIndex);
            }}
          />
        )}
        <ImageUpload
          code={code}
          nextOrder={images.length + 1}
          onAdd={(image) => {
            setImages((currentImages) => [...currentImages, image]);
          }}
        />
      </div>
    </Layout>
  );
}

function SortableImageList({
  images,
  isDisabled,
  onDelete,
  onReorder,
}: {
  images: ImageType[];
  isDisabled: boolean;
  onDelete: (image: ImageType) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const layoutsRef = useRef<ItemLayout[]>([]);
  const dragStateRef = useRef<{
    index: number;
    offsetY: number;
    pointerId: number;
  } | null>(null);

  const [layouts, setLayouts] = useState<ItemLayout[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  const measureLayouts = useCallback(() => {
    const nextLayouts = itemRefs.current.map((item) => {
      if (!item) {
        return { top: 0, height: 0 };
      }
      return {
        top: item.offsetTop,
        height: item.offsetHeight,
      };
    });
    layoutsRef.current = nextLayouts;
    setLayouts(nextLayouts);
    return nextLayouts;
  }, []);

  useLayoutEffect(() => {
    measureLayouts();
  }, [images, measureLayouts]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (dragStateRef.current === null) {
        measureLayouts();
      }
    });

    observer.observe(listElement);
    return () => {
      observer.disconnect();
    };
  }, [measureLayouts]);

  const itemShifts = useMemo(
    () =>
      getItemShifts({
        layouts,
        draggedIndex,
        dropIndex,
      }),
    [layouts, draggedIndex, dropIndex],
  );

  const finishDrag = useCallback(
    (shouldCommit: boolean) => {
      const dragState = dragStateRef.current;
      if (!dragState) {
        return;
      }

      const fromIndex = dragState.index;
      const toIndex = dropIndex;

      dragStateRef.current = null;
      setTransitionEnabled(false);
      setDraggedIndex(null);
      setDropIndex(null);
      setDragOffsetY(0);

      if (shouldCommit && toIndex !== null && fromIndex !== toIndex) {
        onReorder(fromIndex, toIndex);
      }

      requestAnimationFrame(() => {
        measureLayouts();
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
        });
      });
    },
    [dropIndex, measureLayouts, onReorder],
  );

  const handleGripPointerDown = useCallback(
    (index: number, event: React.PointerEvent<HTMLDivElement>) => {
      if (isDisabled) {
        return;
      }

      const item = itemRefs.current[index];
      const listElement = listRef.current;
      if (!item || !listElement) {
        return;
      }

      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      measureLayouts();
      const itemTop = item.getBoundingClientRect().top;

      dragStateRef.current = {
        index,
        offsetY: event.clientY - itemTop,
        pointerId: event.pointerId,
      };
      setDraggedIndex(index);
      setDropIndex(index);
      setDragOffsetY(0);
    },
    [isDisabled, measureLayouts],
  );

  const handleGripPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      const listElement = listRef.current;
      if (
        !dragState ||
        !listElement ||
        event.pointerId !== dragState.pointerId
      ) {
        return;
      }

      const currentLayouts = layoutsRef.current;
      const listTop = listElement.getBoundingClientRect().top;
      const pointerY = event.clientY - listTop;
      const draggedLayout = currentLayouts[dragState.index];

      if (draggedLayout) {
        setDragOffsetY(pointerY - dragState.offsetY - draggedLayout.top);
      }

      setDropIndex(
        getDropIndex({
          layouts: currentLayouts,
          pointerY,
        }),
      );
    },
    [],
  );

  const handleGripPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      finishDrag(true);
    },
    [finishDrag],
  );

  const handleGripPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || event.pointerId !== dragState.pointerId) {
        return;
      }

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      finishDrag(false);
    },
    [finishDrag],
  );

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-200">Images</p>
        <p className="text-xs text-gray-400">Drag to reorder</p>
      </div>
      <div ref={listRef} className="relative flex flex-col gap-2" role="list">
        {images.map((image, index) => {
          const isDragging = draggedIndex === index;
          const shiftY = itemShifts[index] ?? 0;
          const translateY = isDragging ? dragOffsetY : shiftY;

          return (
            <div
              key={image.id.toString()}
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              className={isDragging ? "relative z-20" : "relative z-0"}
              style={{
                transform: `translateY(${translateY}px)`,
                transition:
                  transitionEnabled && !isDragging
                    ? REORDER_TRANSITION
                    : "none",
              }}
            >
              <Image
                image={image}
                index={index}
                isDisabled={isDisabled}
                isDragging={isDragging}
                onDelete={onDelete}
                onGripPointerCancel={handleGripPointerCancel}
                onGripPointerDown={(event) => {
                  handleGripPointerDown(index, event);
                }}
                onGripPointerMove={handleGripPointerMove}
                onGripPointerUp={handleGripPointerUp}
                onMoveDown={() => {
                  if (index < images.length - 1) {
                    onReorder(index, index + 1);
                  }
                }}
                onMoveUp={() => {
                  if (index > 0) {
                    onReorder(index, index - 1);
                  }
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Image({
  image,
  isDisabled,
  index,
  isDragging,
  onDelete,
  onGripPointerCancel,
  onGripPointerDown,
  onGripPointerMove,
  onGripPointerUp,
  onMoveUp,
  onMoveDown,
}: {
  image: ImageType;
  isDisabled: boolean;
  index: number;
  isDragging: boolean;
  onDelete: (image: ImageType) => void;
  onGripPointerCancel: (event: React.PointerEvent<HTMLDivElement>) => void;
  onGripPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onGripPointerMove: (event: React.PointerEvent<HTMLDivElement>) => void;
  onGripPointerUp: (event: React.PointerEvent<HTMLDivElement>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { connection } = useConnection();

  const url = useMemo(() => {
    return URL.createObjectURL(
      new Blob([new Uint8Array(image.bytes)], {
        type: "image/jpeg",
      }),
    );
  }, [image.bytes]);

  const [title, setTitle] = useState(image.title);
  const [titleLoading, setTitleLoading] = useState(false);

  useEffect(() => {
    setTitle(image.title);
  }, [image.title]);

  return (
    <div
      aria-grabbed={isDragging}
      aria-label={`Image ${index + 1}: ${image.title}`}
      className={`flex w-full gap-3 rounded-xl border p-3 ${
        isDragging
          ? "border-blue-400 bg-gray-700/90 shadow-lg"
          : "border-white/15 bg-gray-700/60"
      }`.trim()}
      role="listitem"
    >
      <div
        aria-label={`Drag image ${index + 1} to reorder`}
        className={`flex shrink-0 touch-none flex-col items-center gap-2 pt-1 text-gray-400 ${
          isDisabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-grab active:cursor-grabbing"
        }`.trim()}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onKeyDown={(event) => {
          if (isDisabled) {
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            onMoveUp();
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            onMoveDown();
          }
        }}
        onPointerCancel={onGripPointerCancel}
        onPointerDown={onGripPointerDown}
        onPointerMove={onGripPointerMove}
        onPointerUp={onGripPointerUp}
      >
        <FontAwesomeIcon
          aria-hidden={true}
          className="text-base"
          icon={faGripVertical}
        />
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-600 text-xs font-semibold text-gray-200">
          {index + 1}
        </span>
      </div>
      <div className="relative shrink-0">
        <Button
          className="!absolute top-1 left-1 z-10"
          disabled={isDisabled}
          severity="danger"
          size="sm"
          type="button"
          onClick={() => {
            (async () => {
              try {
                await deleteImage({ connection, imageId: image.id.toString() });
                onDelete(image);
              } catch (error: unknown) {
                console.error(error);
              }
            })();
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </Button>
        <img
          alt={image.title}
          className="h-[120px] w-[120px] rounded-lg bg-gray-800 object-cover"
          draggable={false}
          src={url}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <Form
          className="flex w-full flex-col gap-2 sm:flex-row sm:items-end"
          onsubmit={() => {
            (async () => {
              setTitleLoading(true);
              try {
                await updateImage({
                  connection,
                  imageId: image.id.toString(),
                  image: { title },
                });
              } catch (error: unknown) {
                console.error(error);
              } finally {
                setTitleLoading(false);
              }
            })();
          }}
        >
          <Input
            className="min-w-0 flex-1"
            disabled={isDisabled || titleLoading}
            name="title"
            placeholder="Title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Button
            disabled={isDisabled || titleLoading}
            loading={titleLoading}
            type="submit"
          >
            Save
          </Button>
        </Form>
      </div>
    </div>
  );
}

function ImageUpload({
  code,
  nextOrder,
  onAdd,
}: {
  code: string;
  nextOrder: number;
  onAdd: (image: ImageType) => void;
}) {
  const { connection } = useConnection();

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadKey, setUploadKey] = useState(0);

  const isDisabled = loading || files.length === 0;

  return (
    <Form
      className="w-full self-stretch"
      onsubmit={() => {
        (async () => {
          if (files.length === 0) {
            return;
          }
          setLoading(true);
          try {
            let order = nextOrder;
            for (const file of files) {
              const fileNameWithoutExtension = file.name
                .split(".")
                .slice(0, -1)
                .join(".");
              const resizedFileBuffer = await resizeImageToMax({
                file,
                maxSize: MAX_IMAGE_PIXEL_SIZE,
              });
              const image = await addImage({
                connection,
                code,
                image: {
                  title: fileNameWithoutExtension,
                  order,
                  bytes: resizedFileBuffer,
                },
                fileName: file.name,
              });
              onAdd(image);
              order += 1;
            }
            setFiles([]);
            setUploadKey((key) => key + 1);
          } catch (error: unknown) {
            console.error(error);
          } finally {
            setLoading(false);
          }
        })();
      }}
    >
      <FileUpload
        key={uploadKey}
        allowedExtensions={["jpg", "jpeg", "png", "gif"]}
        onChange={(selectedFiles) => {
          setFiles(selectedFiles);
        }}
      >
        <Button
          className="flex-1"
          disabled={isDisabled}
          loading={loading}
          type="submit"
        >
          Upload
        </Button>
      </FileUpload>
    </Form>
  );
}

async function resizeImageToMax({
  file,
  maxSize,
}: {
  file: File;
  maxSize: number;
}) {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(imageUrl);
    const { width, height } = image;
    const ratio = Math.min(maxSize / width, maxSize / height, 1);
    const targetWidth = Math.max(1, Math.round(width * ratio));
    const targetHeight = Math.max(1, Math.round(height * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      return file.arrayBuffer();
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, file.type || "image/jpeg");
    });

    if (!blob) {
      return file.arrayBuffer();
    }

    return blob.arrayBuffer();
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}
